import { Box, Stack } from '@mui/material';
import {
  allocatedCostByRole,
  createCostRateResolver,
  createSellRateResolver,
  marginPctFromFactor,
  setTargetFactor,
  targetIsOperative,
} from '@timelog/pm-shared';
import type { ContractInput } from '@timelog/pm-shared';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CostByRoleChart } from './CostByRoleChart';
import { parseFactorInput, parseMarginInput, useFinancials } from './editor';
import { factorTrend, formatFactorAs, formatMetricValue } from './metricReading';

import { MaterialSymbol } from '@/components/MaterialSymbol';
import { InlineTextCell } from '@/components/planning/cells';
import { ContractChip } from '@/components/planning/ContractChip';
import { contractStaffing } from '@/components/planning/contractStaffing';
import {
  FigureTooltip,
  KPI_BOX_SX,
  KPI_LABEL_SX,
  KPI_VALUE_SX,
  MICRO_LABEL_TRACKING,
  NoticeChip,
  SectionHeading,
  SidePanelHeading,
} from '@/components/ui';
import type { NoticeTone } from '@/components/ui';
import { factorDigits, formatAmountAbbrev, formatFactor, formatHours, todayIso } from '@/lib';

/**
 * The Financials panel's contract view — what it opens on (3 Nov, Mette: the
 * panel is the active contract's, with "Show project summary" on the collapse
 * row for the whole project, as Planning's task view). Titled with the
 * contract's name, badge and billing method, as a task's name titles
 * Planning's (the name came off the rail on 2026-09-29 because the editor says
 * it too; Mette brought it back for the panels' consistency). Two sections:
 * Budget (the CALC-14 diff chip, budgeted cost, budget factor, target) and
 * Allocation (allocated hours and the forecasted factor from the staffing —
 * what the plan's allocations make of that budget).
 */

/**
 * Cost analysis — the by-role pyramids — is HIDDEN since 2026-09-29 at the
 * pilot's request, kept in the code (toggle, section, test) so it can be
 * recalled by flipping this. It belongs under Allocation when it returns.
 */
const COST_ANALYSIS_ENABLED: boolean = false;

/** The target box is the narrow one at the far right, so its figure is a size down. */
const KPI_TARGET_VALUE_SX = { ...KPI_VALUE_SX, fontSize: 16 } as const;

const SECTION_DIVIDER_SX = {
  pb: 6,
  borderBottom: 'var(--border-width-thin) solid var(--border-divider)',
} as const;

export const ContractAside = () => {
  const {
    state,
    dispatch,
    project,
    activeFinancials,
    budgetModel,
    calcOptions,
    effectiveContracts,
    blendedCostRate,
    consumption,
  } = useFinancials();
  const { t } = useTranslation(['pmFinancials', 'pmCommon']);
  // One evaluation day per mount (D14), like the plan editor's.
  const [today] = useState(todayIso);
  // The display metric (ADR 0027): every ratio here is a factor underneath and
  // reads as factor or margin per the configuration — caption, value, formula.
  const metric = budgetModel.config.metric;
  const [costAnalysisOpen, setCostAnalysisOpen] = useState(false);
  // The contract's own target (seeded from the budget-model config at
  // create, set by double-click here since 2026-10-12 — CALC-27 derives the Own
  // Work factor from it). No marker when it differs from the configured default.
  const target = {
    factor: state.document.targetFactor,
    marginPct: marginPctFromFactor(state.document.targetFactor),
  };
  const [targetEditing, setTargetEditing] = useState(false);
  const commitTarget = (raw: string) => {
    const factor = metric === 'factor' ? parseFactorInput(raw) : parseMarginInput(raw);
    if (factor !== null) {
      const options = calcOptions;
      dispatch({ type: 'APPLY_OP', op: (document) => setTargetFactor(document, factor, options) });
    }
    setTargetEditing(false);
  };
  /**
   * With every section's uplift hidden at one shared value the factor is pinned
   * by construction, so the target is unreachable and the trend would sit
   * permanently on coral "down". Both are suppressed rather than shown as a
   * goal that cannot be met — see `targetIsOperative`, which since schema 2
   * also asks whether the fixed factors differ (then the mix still moves it).
   */
  const showTarget = targetIsOperative(budgetModel.config);
  const forecastedLabel =
    metric === 'factor' ? t('pmCommon:term.ForecastedFactor') : t('pmCommon:term.ForecastedMargin');
  const trend = factorTrend(activeFinancials.budgetedFactor, showTarget ? target.factor : null);
  // CALC-21 by role: the ACTIVE contract's staffing lines costed, grouped by role.
  const costByRole = useMemo(() => {
    const inputs: ContractInput[] = effectiveContracts.map(({ id, name, type, document }) => ({
      id,
      name,
      type,
      document,
    }));
    return allocatedCostByRole(
      project.document,
      inputs,
      { ...calcOptions, today },
      { contractId: state.contractId },
    );
  }, [project.document, effectiveContracts, calcOptions, today, state.contractId]);
  /*
    The ACTIVE contract's forecasted uplift — its Own Work amount over the
    staffing lines' cost on the tasks mapped to it — the same `contractStaffing`
    figure the Planning rail's Contract insights show per contract (2026-09-29,
    the pilot's ask: that tile copied here, beside the budget factor it is
    read against). Priced with the plan-side resolvers over the same options.
  */
  const staffing = useMemo(() => {
    const inputs: ContractInput[] = effectiveContracts.map(({ id, name, type, document }) => ({
      id,
      name,
      type,
      document,
    }));
    const planOptions = { ...calcOptions, today };
    return contractStaffing(
      state.contractId,
      project.document.plan,
      consumption,
      createSellRateResolver(inputs, blendedCostRate, planOptions),
      createCostRateResolver(inputs, blendedCostRate, planOptions),
    );
  }, [
    effectiveContracts,
    calcOptions,
    today,
    state.contractId,
    project.document.plan,
    consumption,
    blendedCostRate,
  ]);

  const diff = activeFinancials.notAllocated;
  /**
   * Whether there is a distribution to report on at all.
   *
   * `notAllocated` (CALC-14) is `revenue − Σ distributed amounts`, so an empty
   * contract and one distributed to the penny are the same number: 0. Without
   * this gate the zero fell into the `ok` branch and an untouched contract
   * claimed "Revenue fully distributed across categories" — a balance that is
   * technically true and practically meaningless.
   *
   * The chip is suppressed rather than given neutral copy, because the tiles
   * beside it already say "empty" with an em dash. This chip and the totals
   * row's "remaining" are the only places the CALC-14 remainder shows — the
   * bento's "Not allocated" card was removed (2026-09-18) as a section that
   * had nothing editable in it.
   */
  const hasRevenueToDistribute = activeFinancials.revenue > 0;
  const diffState = diff > 0 ? 'under' : diff < 0 ? 'over' : 'ok';
  const diffStyle = (
    {
      under: {
        tone: 'warning',
        icon: 'info',
        text: t('aside.DiffUnder', { amount: formatAmountAbbrev(diff) }),
      },
      over: {
        tone: 'coral',
        icon: 'warning',
        text: t('aside.DiffOver', { amount: formatAmountAbbrev(-diff) }),
      },
      ok: {
        tone: 'success',
        icon: 'check_circle',
        text: t('aside.DiffOk'),
      },
    } satisfies Record<typeof diffState, { tone: NoticeTone; icon: string; text: string }>
  )[diffState];

  // How the two factors differ (the pilot's ask): what the budget divides by,
  // and what the staffing does. Under Allocation, or under Budget without it.
  const formulaLine = (
    <Box
      data-automation-id="aside-kpi-formula"
      sx={{ fontSize: 10, color: 'var(--fg-3)', mt: 1, lineHeight: 1.45 }}
    >
      {metric === 'factor' ? t('aside.KpiFormulaFactor') : t('aside.KpiFormulaMargin')}
    </Box>
  );

  return (
    <Box data-automation-id="budget-aside" data-view="contract">
      <Stack
        direction="row"
        data-automation-id="contract-aside-heading"
        sx={{ alignItems: 'center', gap: 1, flexWrap: 'wrap', pt: 2, mb: 4 }}
      >
        <SidePanelHeading name={state.name} nameAutomationId="contract-aside-name" />
        <ContractChip type={state.type} size={17} />
        <Box
          component="span"
          data-automation-id="contract-aside-billing"
          sx={{ fontSize: 11, color: 'var(--fg-3)' }}
        >
          {t(`pmCommon:billingMethod.${state.type}`)}
        </Box>
      </Stack>

      <Box
        data-automation-id="aside-contract-section"
        sx={staffing ? SECTION_DIVIDER_SX : { pb: 2 }}
      >
        <Box sx={{ mb: 2 }}>
          <SectionHeading icon="account_balance_wallet">{t('aside.Budget')}</SectionHeading>
        </Box>
        {hasRevenueToDistribute ? (
          <Box sx={{ mb: 1.5 }}>
            <NoticeChip
              tone={diffStyle.tone}
              icon={diffStyle.icon}
              automationId="aside-diff-chip"
              state={diffState}
            >
              {diffStyle.text}
            </NoticeChip>
          </Box>
        ) : null}

        {/*
          The budget's KPI row (2026-09-29, the pilot's grid, split in two on
          3 Nov): budgeted cost (CALC-12), budget factor or margin with its
          trend, and the configured target (ADR 0027) at the far right —
          absent, like the trend, when it is not operative.
        */}
        <Box
          data-automation-id="contract-kpis"
          sx={{
            display: 'grid',
            gridTemplateColumns: showTarget
              ? 'minmax(0, 1fr) minmax(0, 1fr) minmax(64px, auto)'
              : 'minmax(0, 1fr) minmax(0, 1fr)',
            gap: 1,
            mt: 1.5,
          }}
        >
          <FigureTooltip title={t('help.BudgetedCost')}>
            <Box sx={KPI_BOX_SX} tabIndex={0} data-automation-id="contract-cost-box">
              <Box sx={KPI_LABEL_SX}>{t('categoryCard.BudgetedCost')}</Box>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, flexWrap: 'wrap' }}>
                <Box component="span" data-automation-id="contract-cost" sx={KPI_VALUE_SX}>
                  {formatAmountAbbrev(activeFinancials.cost)}
                </Box>
                <Box component="span" sx={{ fontSize: 11, color: 'var(--fg-3)' }}>
                  {state.document.currency}
                </Box>
              </Box>
            </Box>
          </FigureTooltip>
          <FigureTooltip
            title={metric === 'factor' ? t('help.BudgetFactor') : t('help.BudgetMargin')}
          >
            <Box sx={KPI_BOX_SX} tabIndex={0} data-automation-id="contract-factor-box">
              <Box sx={KPI_LABEL_SX}>
                {metric === 'factor' ? t('aside.BudgetFactor') : t('aside.BudgetMargin')}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box
                  component="span"
                  data-automation-id="contract-factor"
                  data-metric={metric}
                  sx={KPI_VALUE_SX}
                >
                  {formatMetricValue(metric, activeFinancials)}
                </Box>
                <MaterialSymbol name={trend.icon} size={18} color={trend.color} />
              </Box>
            </Box>
          </FigureTooltip>
          {showTarget ? (
            /* The target at the far right, a size down (2026-09-29). */
            <FigureTooltip
              title={metric === 'factor' ? t('help.TargetFactor') : t('help.TargetMargin')}
            >
              <Box
                tabIndex={0}
                data-automation-id="contract-target-box"
                // Double-click sets the target (CALC-27); a single click is nothing,
                // so the box reads as a figure, not a field.
                onDoubleClick={() => setTargetEditing(true)}
                sx={{
                  ...KPI_BOX_SX,
                  px: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}
              >
                {/* "Target factor" / "Målfaktor" (pilot annotation, 2026-10-12). */}
                <Box sx={KPI_LABEL_SX}>
                  {metric === 'factor' ? t('aside.TargetFactor') : t('aside.TargetMargin')}
                </Box>
                <InlineTextCell
                  automationId="contract-target"
                  value={
                    metric === 'factor'
                      ? factorDigits(target.factor)
                      : String(Math.round(target.marginPct * 10) / 10)
                  }
                  editing={targetEditing}
                  onStartEdit={() => undefined}
                  onCancel={() => setTargetEditing(false)}
                  onCommit={commitTarget}
                  inputWidth={56}
                  inputSx={{ ...KPI_TARGET_VALUE_SX }}
                  display={
                    <Box component="span" sx={KPI_TARGET_VALUE_SX}>
                      {metric === 'factor'
                        ? formatFactor(target.factor, 1)
                        : `${String(Math.round(target.marginPct))}%`}
                    </Box>
                  }
                />
              </Box>
            </FigureTooltip>
          ) : null}
        </Box>
        {staffing ? null : formulaLine}
      </Box>

      {staffing ? (
        <Box data-automation-id="aside-allocation-section" sx={{ pt: 6, pb: 2 }}>
          <Box sx={{ mb: 2 }}>
            <SectionHeading icon="groups">{t('aside.Allocation')}</SectionHeading>
          </Box>
          <Box
            data-automation-id="contract-allocation-kpis"
            sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 1 }}
          >
            {staffing ? (
              /*
                Allocated hours (2026-09-29, the pilot's ask): the share of the
                contract's Own Work hours somebody is allocated to (CALC-22 over
                CALC-11), with what is left — or by how much it is over — beside it.
              */
              <FigureTooltip title={t('help.AllocatedHours')}>
                <Box sx={KPI_BOX_SX} tabIndex={0} data-automation-id="contract-allocated-hours">
                  <Box sx={KPI_LABEL_SX}>{t('aside.AllocatedHours')}</Box>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, flexWrap: 'wrap' }}>
                    <Box
                      component="span"
                      data-automation-id="contract-allocated-hours-pct"
                      sx={{
                        ...KPI_VALUE_SX,
                        color:
                          staffing.allocatedPct !== null && staffing.allocatedPct > 100
                            ? 'var(--color-error-80)'
                            : 'var(--fg-1)',
                      }}
                    >
                      {staffing.allocatedPct === null
                        ? '—'
                        : `${String(Math.round(staffing.allocatedPct))}%`}
                    </Box>
                    <Box
                      component="span"
                      data-automation-id="contract-allocated-hours-remaining"
                      sx={{
                        fontSize: 11,
                        // Right-aligned in the box, on the figure's line when it fits.
                        marginLeft: 'auto',
                        textAlign: 'right',
                        color:
                          staffing.allocatedRemaining < 0 ? 'var(--color-error-80)' : 'var(--fg-3)',
                      }}
                    >
                      {staffing.allocatedRemaining >= 0
                        ? t('aside.HoursRemaining', {
                            hours: formatHours(staffing.allocatedRemaining),
                          })
                        : t('aside.HoursOver', {
                            hours: formatHours(-staffing.allocatedRemaining),
                          })}
                    </Box>
                  </Box>
                </Box>
              </FigureTooltip>
            ) : null}
            {staffing ? (
              // Dashed, like the project totals' forecast tile: a projection, not a budgeted figure.
              <FigureTooltip
                title={metric === 'factor' ? t('help.ForecastFactor') : t('help.ForecastMargin')}
              >
                <Box
                  sx={{ ...KPI_BOX_SX, borderStyle: 'dashed' }}
                  tabIndex={0}
                  data-automation-id="contract-forecast-factor"
                >
                  <Box sx={KPI_LABEL_SX}>{forecastedLabel}</Box>
                  {staffing.allocatedCost > 0 ? (
                    <Box component="span" sx={KPI_VALUE_SX}>
                      {formatFactorAs(metric, staffing.uplift)}
                    </Box>
                  ) : (
                    // No dash: the hint alone is the box's content until there is a cost.
                    <Box
                      data-automation-id="contract-forecast-factor-hint"
                      sx={{ fontSize: 10, color: 'var(--fg-3)', lineHeight: 1.35 }}
                    >
                      {t('aside.ForecastHint')}
                    </Box>
                  )}
                </Box>
              </FigureTooltip>
            ) : null}
          </Box>
          {formulaLine}
          {COST_ANALYSIS_ENABLED ? (
            <>
              {/*
                Cost analysis: allocated by role (CALC-21 by role) for THIS contract,
                folded by default so the summary stays a glance.
              */}
              <Box
                component="button"
                type="button"
                data-automation-id="aside-cost-analysis-toggle"
                aria-expanded={costAnalysisOpen}
                onClick={() => setCostAnalysisOpen((open) => !open)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  mt: 1.5,
                  px: 1,
                  py: 0.5,
                  border: 'var(--border-width-thin) solid var(--border-divider)',
                  borderRadius: 'var(--border-radius-pill)',
                  bgcolor: costAnalysisOpen ? 'var(--bg-sunken)' : 'transparent',
                  fontSize: 11,
                  fontFamily: 'var(--font-family)',
                  color: 'var(--fg-2)',
                  cursor: 'pointer',
                  '&:hover': { borderColor: 'var(--border-subtle)', color: 'var(--fg-1)' },
                  '&:focus-visible': { outline: 'var(--focus-ring)', outlineOffset: '1px' },
                }}
              >
                <MaterialSymbol name={costAnalysisOpen ? 'expand_less' : 'expand_more'} size={14} />
                {t('aside.CostAnalysis')}
              </Box>
              {costAnalysisOpen ? (
                <Box data-automation-id="aside-cost-analysis">
                  {/*
                  Allocated by role (CALC-21 by role), for THIS contract: the staffing
                  lines on the tasks mapped to it, as two pyramids — hours and cost —
                  ordered by the role's cost per hour, dearest at the top.
                */}
                  <Box sx={{ mt: 2 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'baseline',
                        justifyContent: 'space-between',
                        mb: 1,
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          fontSize: 11,
                          fontWeight: 500,
                          letterSpacing: MICRO_LABEL_TRACKING,
                          textTransform: 'uppercase',
                          color: 'var(--fg-3)',
                        }}
                      >
                        {t('aside.AllocatedByRole')}
                      </Box>
                      <Box component="span" sx={{ fontSize: 11, color: 'var(--fg-3)' }}>
                        {t('aside.CostliestFirst')}
                      </Box>
                    </Box>
                    <CostByRoleChart rows={costByRole} />
                  </Box>
                </Box>
              ) : null}
            </>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
};
